import { asCur } from "./inputHelpers";
import { isIndexable } from "./typeHelpers";

export function sumInObject(obj?: Record<string, unknown>) {
	const sum = Object.getOwnPropertyNames(obj ?? {}).reduce((sum, prop) => {
		const value = obj?.[prop];

		const inputValueAsNumber = asCur(value);

		if (inputValueAsNumber === undefined) {
			return sum;
		}

		return sum + inputValueAsNumber;
	}, 0);

	return sum;
}

export function containSameElements<T>(array1: T[], array2: T[]) {
	function allElementsIn2(a1: T[], a2: T[]): boolean {
		return a1.every((element) => {
			if (a2.includes(element)) {
				return true;
			}

			return false;
		});
	}

	if (array1.length === array2.length) {
		return allElementsIn2(array1, array2) && allElementsIn2(array2, array1);
	}

	return false;
}

export function arrayContains<T>(elem: T, array: T[]) {
	return array.indexOf(elem) > -1;
}

export function getAndDeleteValueFromObjectRecursive(
	obj: unknown,
	path?: string[] | null,
): unknown {
	if (!isIndexable(obj) || !path) {
		return undefined;
	}

	const nextKey = path[0];
	if (!nextKey) {
		return undefined;
	}

	if (path.length === 1) {
		if (nextKey in obj) {
			const value = obj?.[nextKey];
			delete obj?.[nextKey];
			return value;
		}
		return undefined;
	}

	return nextKey in obj
		? getAndDeleteValueFromObjectRecursive(obj[nextKey], getShiftCopy(path))
		: undefined;
}

export function assignValueToObjectRecursive(
	obj: unknown,
	path: string[],
	value: unknown,
) {
	if (!isIndexable(obj)) {
		throw new Error(
			"Obj was null or undefined. Can't assign a value to null or undefined.",
		);
	}

	if (path === null || path === undefined) {
		throw new Error("Can't assign to a null or undefined path.");
	}

	const nextPath = path[0];
	if (!nextPath) {
		throw new Error("Path must have a length greater than 0.");
	}

	if (path.length === 1) {
		obj[nextPath] = value;
		return;
	}

	obj[nextPath] ??= {};

	assignValueToObjectRecursive(obj[nextPath], getShiftCopy(path), value);
}

function getShiftCopy<T>(arr: T[]): T[] {
	const newPath = [...arr];
	newPath.shift();

	return newPath;
}

export function getPseudoRandomId(): string {
	return Math.floor(Math.random() * 1000000000).toString();
}

export function localeCompare(a: string, b: string) {
	return a.localeCompare(b);
}
